import React, {
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

import focusRing from '../../theme/focus';
import {
  AnswerFeedback,
  AnswerFeedbackReason,
  answerFeedbackReasons,
} from './types';
import controlSx from './answerControlSx';
import PinguCopyAnswer from './PinguCopyAnswer';

const SAVE_FAILED_MS = 4_000;

const REASON_LABELS: { reason: AnswerFeedbackReason; label: string }[] = [
  { reason: answerFeedbackReasons.WRONG_ANSWER, label: 'Wrong answer' },
  { reason: answerFeedbackReasons.MISSING_DATA, label: 'Missing data' },
  { reason: answerFeedbackReasons.MISUNDERSTOOD, label: 'Misunderstood me' },
  { reason: answerFeedbackReasons.SOMETHING_ELSE, label: 'Something else' },
];

const PinguAnswerFeedback = ({
  feedback,
  pinned,
  canRate,
  getAnswerElement,
  copyDisabled,
  onRate,
  onReport,
  onWithdraw,
}: {
  feedback?: AnswerFeedback;
  pinned?: boolean;
  canRate: boolean;
  getAnswerElement: () => HTMLElement | null;
  copyDisabled?: boolean;
  onRate: () => Promise<boolean>;
  onReport: (reason: AnswerFeedbackReason, comment: string) => Promise<boolean>;
  onWithdraw: () => Promise<boolean>;
}) => {
  const questionId = useId();
  const commentId = useId();
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState<AnswerFeedbackReason | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // What the row shows while a save is in flight; null stands for no rating.
  const [pendingFeedback, setPendingFeedback] = useState<AnswerFeedback | null | undefined>();
  const reportRef = useRef<HTMLDivElement>(null);
  const radioRefs = useRef<(HTMLDivElement | null)[]>([]);
  const shownFeedback = pendingFeedback === undefined ? feedback : pendingFeedback ?? undefined;
  const selectedIndex = REASON_LABELS.findIndex(({ reason: value }) => value === reason);
  const focusableIndex = selectedIndex === -1 ? 0 : selectedIndex;
  const upSelected = shownFeedback?.rating === 'positive' && !reportOpen;

  // The panel opens taller than the gap left above the composer, so its send
  // control would otherwise sit below the fold.
  useEffect(() => {
    if (reportOpen) reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [reportOpen]);

  useEffect(() => {
    if (!saveFailed) return undefined;
    const timer = window.setTimeout(() => setSaveFailed(false), SAVE_FAILED_MS);
    return () => window.clearTimeout(timer);
  }, [saveFailed]);

  const save = async (
    next: AnswerFeedback | null,
    request: () => Promise<boolean>,
  ): Promise<boolean> => {
    setPendingFeedback(next);
    setSaving(true);
    setSaveFailed(false);
    const saved = await request();
    setSaving(false);
    setPendingFeedback(undefined);
    if (!saved) setSaveFailed(true);
    return saved;
  };

  const closeReport = () => {
    setReportOpen(false);
    setReason(null);
    setComment('');
  };

  const submitReport = async () => {
    if (!reason) return;

    const trimmedComment = comment.trim();
    setReportOpen(false);
    const saved = await save(
      { rating: 'negative', reason, comment: trimmedComment },
      () => onReport(reason, trimmedComment),
    );
    if (saved) closeReport();
    else setReportOpen(true);
  };

  const handleGoodAnswer = () => {
    if (reportOpen) {
      closeReport();
      if (shownFeedback?.rating !== 'positive') save({ rating: 'positive' }, onRate);
      return;
    }
    if (shownFeedback?.rating === 'positive') save(null, onWithdraw);
    else save({ rating: 'positive' }, onRate);
  };

  const handleReasonKeyDown = (event: React.KeyboardEvent, index: number) => {
    const last = REASON_LABELS.length - 1;
    let next: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    if (next !== null) {
      event.preventDefault();
      setReason(REASON_LABELS[next].reason);
      radioRefs.current[next]?.focus();
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      setReason(REASON_LABELS[index].reason);
    }
  };

  const saveFailedMessage = saveFailed && (
    <Typography variant="body1" color="text.secondary" role="status">
      Couldn&apos;t save. Try again.
    </Typography>
  );

  const copy = <PinguCopyAnswer getAnswerElement={getAnswerElement} disabled={copyDisabled} />;

  if (shownFeedback?.rating === 'negative') {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        {copy}
        <Typography variant="body1" color="text.secondary">Reported. Thank you.</Typography>
        <Button
          size="small"
          variant="text"
          disabled={saving}
          onClick={() => { save(null, onWithdraw); }}
          sx={controlSx}
        >
          Undo
        </Button>
        {saveFailedMessage}
      </Stack>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        className="pinguFeedbackRow"
        sx={{
          opacity: shownFeedback || reportOpen || pinned || saveFailed ? 1 : 0,
          transition: 'opacity 120ms ease-out',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '&:hover, &:focus-within': { opacity: 1 },
        }}
      >
        {copy}
        {canRate && (
          <>
            <Tooltip title="Good answer">
              <span>
                <IconButton
                  size="small"
                  aria-label="Good answer"
                  aria-pressed={upSelected}
                  disabled={saving}
                  onClick={handleGoodAnswer}
                  sx={controlSx}
                >
                  {upSelected
                    ? <ThumbUpIcon fontSize="small" />
                    : <ThumbUpOutlinedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Report a problem">
              <span>
                <IconButton
                  size="small"
                  aria-label="Report a problem"
                  aria-expanded={reportOpen}
                  disabled={saving}
                  onClick={() => (reportOpen ? closeReport() : setReportOpen(true))}
                  sx={controlSx}
                >
                  {reportOpen
                    ? <ThumbDownIcon fontSize="small" />
                    : <ThumbDownOutlinedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        {saveFailedMessage && <Box sx={{ pl: 1 }}>{saveFailedMessage}</Box>}
      </Stack>

      {reportOpen && (
        <Box
          ref={reportRef}
          sx={{
            mt: 2,
            p: 3,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.default',
          }}
        >
          <Typography id={questionId} variant="body1" color="text.primary" component="p" sx={{ mb: 2 }}>
            What went wrong?
            {' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>Choose one.</Box>
          </Typography>
          <Box
            role="radiogroup"
            aria-labelledby={questionId}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 2,
              mb: 3,
            }}
          >
            {REASON_LABELS.map(({ reason: value, label }, index) => (
              <Chip
                key={value}
                ref={(el) => { radioRefs.current[index] = el; }}
                label={label}
                size="small"
                role="radio"
                aria-checked={reason === value}
                tabIndex={index === focusableIndex ? 0 : -1}
                onClick={() => setReason(value)}
                onKeyDown={(event) => handleReasonKeyDown(event, index)}
                sx={{
                  width: '100%',
                  backgroundColor: reason === value ? 'background.expand' : 'transparent',
                  border: (theme) => `1px solid ${reason === value
                    ? theme.palette.emphasisText.main
                    : theme.palette.divider}`,
                  color: reason === value ? 'emphasisText.main' : 'text.secondary',
                  '&:focus-visible': { boxShadow: focusRing },
                }}
              />
            ))}
          </Box>
          <Typography
            component="label"
            htmlFor={commentId}
            variant="body1"
            color="text.primary"
            sx={{ display: 'block', mb: 1 }}
          >
            Anything else?
            {' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>(optional)</Box>
          </Typography>
          <Box
            sx={{
              px: 3,
              py: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              backgroundColor: 'background.paper',
              '&:focus-within': { borderColor: 'emphasisText.main' },
            }}
          >
            <InputBase
              id={commentId}
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              sx={{ typography: 'body1', p: 0 }}
            />
          </Box>
          <Typography variant="body1" color="text.secondary" component="p" sx={{ mt: 2 }}>
            Sending this shares the conversation with Pingwire.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button size="small" variant="text" onClick={closeReport} sx={controlSx}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!reason || saving}
              onClick={() => { submitReport(); }}
              sx={{ '&:focus-visible': { boxShadow: focusRing } }}
            >
              Send report
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PinguAnswerFeedback;
